import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AUTH_ERROR,
  isValidNip,
  pickLang,
  pseudoProblem,
  PSEUDO_MAX_LENGTH,
  PSEUDO_MIN_LENGTH,
  type AppConfigResponse,
  type PseudoProblem,
} from "@shared";
import { ApiFetchError, apiFetch, NETWORK_ERROR_CODE } from "../api/client";
import { currentLang } from "../i18n";
import { useSession } from "../auth/SessionProvider";
import LanguageSwitcher from "../components/LanguageSwitcher";
import NipInput from "../components/NipInput";

/** Ecrans successifs : saisie, puis confirmation si le pseudo est inconnu. */
type Step = { kind: "form" } | { kind: "confirmCreate" };

/**
 * Connexion du jeune. Un seul formulaire : pseudo + NIP.
 *
 * Si le pseudo n'existe pas, on ne cree PAS le compte en silence — une faute
 * de frappe donnerait un explorateur vide avec zero point, sans que le jeune
 * comprenne pourquoi son score a disparu. On demande donc confirmation.
 */
export default function Login() {
  const { t } = useTranslation();
  const { login, register } = useSession();

  const [pseudo, setPseudo] = useState("");
  const [nip, setNip] = useState("");
  const [step, setStep] = useState<Step>({ kind: "form" });
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Pourquoi le nom est refusé, affiché dans une fenêtre : un bouton grisé sans
  // explication laisse un jeune de six ans devant un écran qui ne réagit pas.
  const [problem, setProblem] = useState<PseudoProblem | null>(null);
  const [config, setConfig] = useState<AppConfigResponse | null>(null);
  const lang = currentLang();

  // Titre et sous-titre définis par l'admin, avant toute connexion. Une erreur
  // ici est sans conséquence : on retombe sur les traductions embarquées.
  useEffect(() => {
    let cancelled = false;
    apiFetch<AppConfigResponse>("/config")
      .then((data) => {
        if (!cancelled) setConfig(data);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const title = config?.appTitle != null ? pickLang(config.appTitle, lang) : t("app.title");
  const subtitle = config?.appSubtitle != null ? pickLang(config.appSubtitle, lang) : null;

  // Le nom n'est validé qu'au clic : le bouton doit rester actif pour que la
  // fenêtre d'explication puisse s'ouvrir. Seul un nom vide le grise.
  const canSubmit = pseudo.trim() !== "" && isValidNip(nip) && !busy;

  /** Traduit un code d'erreur serveur en clé i18n. */
  function messageKeyFor(error: unknown): string {
    if (!(error instanceof ApiFetchError)) return "login.error.unexpected";
    switch (error.code) {
      case AUTH_ERROR.badCredentials:
        return "login.error.badNip";
      case AUTH_ERROR.pseudoTaken:
        return "login.error.pseudoTaken";
      case AUTH_ERROR.invalidPseudo:
        return "login.error.invalidPseudo";
      case AUTH_ERROR.invalidNip:
        return "login.error.invalidNip";
      case AUTH_ERROR.tooManyAttempts:
        return "login.error.tooManyAttempts";
      case NETWORK_ERROR_CODE:
        return "login.error.offline";
      default:
        return "login.error.unexpected";
    }
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;

    const found = pseudoProblem(pseudo);
    if (found !== null) {
      setProblem(found);
      return;
    }

    setBusy(true);
    setErrorKey(null);
    try {
      await login({ pseudo, nip });
    } catch (error) {
      if (error instanceof ApiFetchError && error.code === AUTH_ERROR.unknownPseudo) {
        setStep({ kind: "confirmCreate" });
      } else {
        setErrorKey(messageKeyFor(error));
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirmCreate() {
    setBusy(true);
    setErrorKey(null);
    try {
      await register({ pseudo, nip });
    } catch (error) {
      setErrorKey(messageKeyFor(error));
      setStep({ kind: "form" });
    } finally {
      setBusy(false);
    }
  }

  if (step.kind === "confirmCreate") {
    return (
      <main className="screen">
        <header className="screen__header">
          <h1 className="screen__title">{title}</h1>
        </header>

        <div className="card">
          <h2 className="card__title">{t("login.confirm.title", { pseudo })}</h2>
          <p className="card__text">{t("login.confirm.text")}</p>

          <div className="button-row">
            <button
              type="button"
              className="button button--primary"
              disabled={busy}
              onClick={() => void handleConfirmCreate()}
            >
              {t("login.confirm.yes")}
            </button>
            <button
              type="button"
              className="button button--ghost"
              disabled={busy}
              onClick={() => {
                setStep({ kind: "form" });
              }}
            >
              {t("login.confirm.no")}
            </button>
          </div>

          {errorKey !== null && <p className="form__error">{t(errorKey)}</p>}
        </div>

        <footer className="screen__footer">
          <LanguageSwitcher />
        </footer>
      </main>
    );
  }

  return (
    <main className="screen">
      <header className="screen__header">
        <h1 className="screen__title">{title}</h1>
        {subtitle !== null && <p className="screen__tagline">{subtitle}</p>}
      </header>

      <form className="card" onSubmit={(e) => void handleSubmit(e)}>
        {/* Dit d'emblée où l'on est ET ce que le formulaire fait : la même page
            sert à créer un accès et à le retrouver, ce qui n'a rien d'évident
            quand on arrive dessus pour la première fois. */}
        <h2 className="card__title">{t("login.heading")}</h2>
        <p className="card__text card__text--muted">{t("login.subheading")}</p>

        <label className="form__label" htmlFor="pseudo">
          {t("login.pseudoLabel")}
        </label>
        <input
          id="pseudo"
          className="form__input"
          type="text"
          value={pseudo}
          // Plafond large, volontairement au-dessus de la limite : un champ qui
          // bloque la frappe en silence n'explique rien. C'est la fenêtre qui
          // dit « trop long ». Ce plafond-ci n'évite qu'un collage géant.
          maxLength={100}
          autoComplete="username"
          autoCapitalize="words"
          placeholder={t("login.pseudoPlaceholder")}
          onChange={(e) => {
            setPseudo(e.target.value);
            setErrorKey(null);
          }}
        />

        <label className="form__label" htmlFor="nip-0">
          {t("login.nipLabel")}
        </label>
        <NipInput
          value={nip}
          onChange={(next) => {
            setNip(next);
            setErrorKey(null);
          }}
        />

        {errorKey !== null && <p className="form__error">{t(errorKey)}</p>}

        <button type="submit" className="button button--primary button--full" disabled={!canSubmit}>
          {busy ? t("login.submitting") : t("login.submit")}
        </button>
      </form>

      <footer className="screen__footer">
        <LanguageSwitcher />
      </footer>

      {problem !== null && (
        <div
          className="dialog__backdrop"
          role="presentation"
          onClick={() => {
            setProblem(null);
          }}
        >
          <div
            className="dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="pseudo-problem-title"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <h3 className="dialog__title" id="pseudo-problem-title">
              {t("login.pseudoProblem.title")}
            </h3>
            <p className="card__text">
              {t(`login.pseudoProblem.${problem}`, {
                min: PSEUDO_MIN_LENGTH,
                max: PSEUDO_MAX_LENGTH,
              })}
            </p>
            <button
              type="button"
              className="button button--primary button--full"
              autoFocus
              onClick={() => {
                setProblem(null);
              }}
            >
              {t("login.pseudoProblem.ok")}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
